import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  COARSE_POINTER_QUERY,
  canUseTouchControls,
} from '../../app/startOptions';
import { trackEvent } from '../../app/telemetry';
import { readMaxScore, writeMaxScore } from '../../shared/storage/localRecords';
import GameController from '../../shared/controls/GameController';
import PhaserGame from '../../shared/phaser/PhaserGame';
import { rangerDefinition } from './rangerDefinition';
import { createRangerGameConfig } from './createRangerGameConfig';
import { getRangerClassConfig } from './rangerClasses';
import {
  readRangerRecords,
  updateRangerRecordsForMission,
  writeRangerRecords,
} from './rangerRecords';
import './RangerGame.css';

const PLAYABLE_ICON_NAMES = {
  ranger: 'Ranger',
  'ranger-scout': 'Spy',
  'ranger-spy': 'Spy',
  'ranger-medic': 'Medic',
  'ranger-heavy': 'Heavy',
};
const NEXT_MISSION_DELAY_MS = 1800;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export function createRangerControllerInput(currentInput = {}, nextInput = {}) {
  return {
    ...nextInput,
    meleeQueued: Boolean(currentInput.meleeQueued || (nextInput.melee && !currentInput.melee)),
  };
}

export function saveBestScoreIfNeeded({
  score,
  bestScore,
  storage,
  namespace,
  writeScore = writeMaxScore,
}) {
  if (score <= bestScore) {
    return bestScore;
  }

  writeScore(score, storage, namespace);
  return score;
}

function readPrefersReducedMotion() {
  return window.matchMedia?.(REDUCED_MOTION_QUERY)?.matches ?? false;
}

export function formatMissionTime(elapsedMs = 0) {
  const seconds = Math.max(0, Math.round(elapsedMs / 1000));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;

  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}

export function getUnlockedMissionIds(records, missions) {
  const completed = records?.normal?.completedMissions ?? [];
  const unlocked = new Set([missions[0]?.id, ...completed]);

  completed.forEach((missionId) => {
    const missionIndex = missions.findIndex((mission) => mission.id === missionId);
    const nextMission = missions[missionIndex + 1];

    if (nextMission) {
      unlocked.add(nextMission.id);
    }
  });

  return missions
    .filter((mission) => unlocked.has(mission.id))
    .map((mission) => mission.id);
}

function RangerGame({
  definition = rangerDefinition,
  onReturnToLibrary,
  selectedPlayableIcon = 'ranger',
}) {
  const storageNamespace = definition.storageNamespace ?? 'ranger';
  const [status, setStatus] = useState('ready');
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(() => readMaxScore(window.localStorage, storageNamespace));
  const [records, setRecords] = useState(() => readRangerRecords(window.localStorage));
  const [levelIndex, setLevelIndex] = useState(0);
  const [instanceKey, setInstanceKey] = useState(0);
  const [missionResult, setMissionResult] = useState(null);
  const [showControlsPanel, setShowControlsPanel] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(readPrefersReducedMotion);
  const [showTouchControls, setShowTouchControls] = useState(canUseTouchControls);
  const classConfig = getRangerClassConfig(selectedPlayableIcon);
  const [lives, setLives] = useState(classConfig.lives);
  const controllerInputRef = useRef({ moveX: 0, moveY: 0, aimX: 0, aimY: 0, primary: false, secondary: false, melee: false, meleeQueued: false, pause: false, paused: false });
  const lastMissionTelemetryKeyRef = useRef(null);

  const selectedIconName = PLAYABLE_ICON_NAMES[selectedPlayableIcon] ?? 'Ranger';
  const missions = useMemo(
    () => definition.missions ?? [],
    [definition.missions]
  );
  const activeMission = missions[levelIndex] ?? missions[0];
  const isFinalMission = levelIndex >= missions.length - 1;
  const isPaused = status === 'paused';
  const isMissionComplete = status === 'missionComplete';
  const isGameOver = status === 'gameOver';
  const isActive = status === 'ready';
  const unlockedMissionIds = useMemo(
    () => getUnlockedMissionIds(records, missions),
    [missions, records]
  );
  const activeMissionRecord = records.normal.bestByMission?.[activeMission?.id] ?? 0;
  const activeMissionBestTime = records.normal.fastestByMission?.[activeMission?.id];
  const activeMissionFewestAlerts = records.normal.fewestAlertsByMission?.[activeMission?.id];

  const resetControllerInput = useCallback((paused = false) => {
    controllerInputRef.current = {
      moveX: 0,
      moveY: 0,
      aimX: 0,
      aimY: 0,
      primary: false,
      secondary: false,
      melee: false,
      meleeQueued: false,
      pause: false,
      paused,
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.(COARSE_POINTER_QUERY);

    if (!mediaQuery) {
      return undefined;
    }

    const updateTouchControls = () => {
      setShowTouchControls(canUseTouchControls());
    };

    mediaQuery.addEventListener?.('change', updateTouchControls);
    mediaQuery.addListener?.(updateTouchControls);

    return () => {
      mediaQuery.removeEventListener?.('change', updateTouchControls);
      mediaQuery.removeListener?.(updateTouchControls);
    };
  }, [resetControllerInput]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape' && event.key.toLowerCase() !== 'p') {
        return;
      }

      setStatus((currentStatus) => {
        if (currentStatus === 'missionComplete') {
          return currentStatus;
        }
        if (currentStatus === 'gameOver') {
          return currentStatus;
        }

        const nextStatus = currentStatus === 'paused' ? 'ready' : 'paused';
        resetControllerInput(nextStatus === 'paused');

        return nextStatus;
      });
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [resetControllerInput]);

  useEffect(() => {
    controllerInputRef.current = {
      ...controllerInputRef.current,
      paused: isPaused || isMissionComplete || isGameOver,
    };
  }, [isGameOver, isPaused, isMissionComplete]);

  useEffect(() => {
    setLives(classConfig.lives);
  }, [classConfig.lives]);

  useEffect(() => {
    trackEvent('ranger.class_selected', {
      classId: selectedPlayableIcon,
      gameId: definition.id,
      lives: classConfig.lives,
      speedMultiplier: classConfig.speedMultiplier,
    });
  }, [classConfig.lives, classConfig.speedMultiplier, definition.id, selectedPlayableIcon]);

  useEffect(() => {
    const missionKey = `${levelIndex}:${instanceKey}:${selectedPlayableIcon}`;

    if (status !== 'ready' || lastMissionTelemetryKeyRef.current === missionKey) {
      return;
    }

    lastMissionTelemetryKeyRef.current = missionKey;
    trackEvent('ranger.mission_started', {
      classId: selectedPlayableIcon,
      gameId: definition.id,
      levelIndex,
      missionId: activeMission?.id,
    });
  }, [activeMission?.id, definition.id, instanceKey, levelIndex, selectedPlayableIcon, status]);

  const onMissionComplete = useCallback((payload) => {
    const nextScore = payload.score;

    trackEvent('ranger.mission_completed', {
      alertCount: payload.alertCount,
      classId: selectedPlayableIcon,
      elapsedMs: payload.elapsedMs,
      gameId: definition.id,
      levelIndex: payload.levelIndex,
      missionId: payload.missionId,
      rank: payload.rank,
      score: nextScore,
    });
    setMissionResult(payload);
    setScore(nextScore);
    setStatus('missionComplete');

    const nextRecords = updateRangerRecordsForMission(records, payload);
    writeRangerRecords(nextRecords, window.localStorage);
    setRecords(nextRecords);

    const nextBestScore = saveBestScoreIfNeeded({
      score: nextScore,
      bestScore,
      storage: window.localStorage,
      namespace: storageNamespace,
    });

    if (nextBestScore !== bestScore) {
      setBestScore(nextBestScore);
    }
  }, [bestScore, definition.id, records, selectedPlayableIcon, storageNamespace]);

  const createConfig = useCallback(({ Phaser }) => createRangerGameConfig({
    Phaser,
    controllerInputRef,
    initialLives: classConfig.lives,
    levelIndex,
    onGameOver: () => {
      resetControllerInput(true);
      trackEvent('ranger.game_over', {
        classId: selectedPlayableIcon,
        gameId: definition.id,
        levelIndex,
      });
      setStatus('gameOver');
    },
    onLivesChange: setLives,
    onMissionComplete,
    reducedMotion,
    selectedPlayableIcon,
  }), [classConfig.lives, definition.id, levelIndex, onMissionComplete, reducedMotion, resetControllerInput, selectedPlayableIcon]);

  const objectiveText = useMemo(
    () => activeMission?.objectiveText ?? 'Complete the mission objective.',
    [activeMission]
  );

  const restartMission = () => {
    resetControllerInput(false);
    setStatus('ready');
    setScore(0);
    setMissionResult(null);
    setLives(classConfig.lives);
    setInstanceKey((current) => current + 1);
  };

  const startNextMission = useCallback(() => {
    resetControllerInput(false);
    setStatus('ready');
    setScore(0);
    setMissionResult(null);
    setLives(classConfig.lives);
    setLevelIndex((current) => Math.min(current + 1, missions.length - 1));
    setInstanceKey((current) => current + 1);
  }, [classConfig.lives, missions.length, resetControllerInput]);

  const startMissionByIndex = useCallback((nextLevelIndex) => {
    resetControllerInput(false);
    setStatus('ready');
    setScore(0);
    setMissionResult(null);
    setShowControlsPanel(false);
    setLives(classConfig.lives);
    setLevelIndex(nextLevelIndex);
    setInstanceKey((current) => current + 1);
  }, [classConfig.lives, resetControllerInput]);

  const pauseMission = () => {
    resetControllerInput(true);
    setStatus('paused');
  };

  const resumeMission = () => {
    resetControllerInput(false);
    setStatus('ready');
  };

  useEffect(() => {
    if (!isMissionComplete || isFinalMission || showControlsPanel) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      startNextMission();
    }, NEXT_MISSION_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isFinalMission, isMissionComplete, showControlsPanel, startNextMission]);

  return (
    <main className="game-app ranger-app">
      <section className="game-shell ranger-shell" aria-labelledby="app-title">
        <header className="hud ranger-hud">
          <div className="hud-title">
            <h1 id="app-title">{definition.name}</h1>
            <p>{objectiveText}</p>
          </div>
          <div className="hud-stats" aria-live="polite">
            <div className="score-panel">
              <span>Mission</span>
              <strong>{levelIndex + 1}/{missions.length}</strong>
            </div>
            <div className="score-panel">
              <span>Class</span>
              <strong>{selectedIconName}</strong>
            </div>
            <div className="score-panel">
              <span>Status</span>
              <strong>
                {isGameOver
                  ? 'Game Over'
                  : isMissionComplete
                    ? 'Complete'
                    : isPaused
                      ? 'Paused'
                      : 'Active'}
              </strong>
            </div>
            <div className="score-panel">
              <span>Lives</span>
              <strong>{lives}</strong>
            </div>
            <div className="score-panel">
              <span>Score</span>
              <strong>{score}</strong>
            </div>
            <div className="score-panel">
              <span>Best</span>
              <strong>{bestScore}</strong>
            </div>
            <div className="score-panel">
              <span>Mission Best</span>
              <strong>{activeMissionRecord}</strong>
            </div>
            <div className="score-panel">
              <span>Rank</span>
              <strong>{missionResult?.rank ?? records.normal.badges?.[activeMission?.id] ?? '-'}</strong>
            </div>
          </div>
        </header>

        <PhaserGame
          key={`${selectedPlayableIcon}-${instanceKey}`}
          ariaLabel={definition.playfieldLabel}
          className="ranger-phaser-field"
          createConfig={createConfig}
        />

        {isPaused || isMissionComplete || isGameOver ? (
          <div className={`ranger-overlay ranger-overlay-${status}`} role="dialog" aria-live="polite" aria-label="Ranger mission menu">
            <div className="ranger-overlay-panel">
              <p>{isGameOver ? 'Game Over' : isPaused ? 'Paused' : 'Mission Complete'}</p>
              <strong>
                {isGameOver
                  ? 'Mission failed'
                  : isMissionComplete
                    ? isFinalMission
                      ? 'All missions cleared'
                      : 'Advancing to next mission'
                    : activeMission?.name ?? 'Current mission'}
              </strong>
              {isMissionComplete && !isFinalMission ? (
                <span>Next mission starts automatically.</span>
              ) : null}
              {missionResult ? (
                <div className="ranger-score-breakdown" aria-label="Mission score breakdown">
                  <span>Rank {missionResult.rank}</span>
                  <span>Time {formatMissionTime(missionResult.elapsedMs)} (-{missionResult.scoreBreakdown.timePenalty})</span>
                  <span>Alerts {missionResult.alertCount} (-{missionResult.scoreBreakdown.alertPenaltyPoints})</span>
                  <span>Defeats {missionResult.enemyDefeatCount} (+{missionResult.scoreBreakdown.enemyDefeatBonusPoints})</span>
                </div>
              ) : null}
              <div className="ranger-mission-select">
                <label htmlFor="ranger-mission-select">Mission Select</label>
                <select
                  id="ranger-mission-select"
                  value={levelIndex}
                  onChange={(event) => startMissionByIndex(Number(event.target.value))}
                >
                  {missions.map((mission, index) => (
                    <option
                      disabled={!unlockedMissionIds.includes(mission.id)}
                      key={mission.id}
                      value={index}
                    >
                      {index + 1}. {mission.name}
                    </option>
                  ))}
                </select>
                <span>
                  Best {activeMissionRecord}
                  {activeMissionBestTime ? ` | Fast ${formatMissionTime(activeMissionBestTime)}` : ''}
                  {Number.isFinite(activeMissionFewestAlerts) ? ` | Alerts ${activeMissionFewestAlerts}` : ''}
                </span>
              </div>
              <button type="button" onClick={() => setShowControlsPanel((current) => !current)}>
                {showControlsPanel ? 'Hide Controls' : 'Controls'}
              </button>
              {showControlsPanel ? (
                <div className="ranger-controls-panel" aria-label="Ranger controls">
                  <span>Move: WASD/arrows or touch joystick</span>
                  <span>Aim: movement direction or last movement</span>
                  <span>Fire: Space or Fire</span>
                  <span>Grenade: E/Shift or Grenade</span>
                  <span>Bayonet: F or Bayonet</span>
                  <label className="ranger-toggle">
                    <input
                      type="checkbox"
                      checked={reducedMotion}
                      onChange={(event) => setReducedMotion(event.target.checked)}
                    />
                    Reduced motion
                  </label>
                </div>
              ) : null}
              {isPaused ? (
                <button type="button" onClick={resumeMission}>
                  Resume
                </button>
              ) : null}
              <button type="button" onClick={restartMission}>
                Restart Mission
              </button>
              {onReturnToLibrary ? (
                <button className="secondary-action" type="button" onClick={onReturnToLibrary}>
                  Game Menu
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {showTouchControls && isActive ? (
          <GameController
            actions={[
              { control: 'primary', label: 'Fire rifle', text: 'Fire', className: 'ranger-fire-action' },
              { control: 'secondary', label: 'Throw grenade', text: 'Grenade', className: 'ranger-grenade-action' },
              { control: 'melee', label: 'Bayonet strike', text: 'Bayonet', className: 'ranger-melee-action' },
              { control: 'pause', label: 'Pause mission', text: 'Pause', className: 'ranger-pause-action' },
            ]}
            ariaLabel="Ranger touch controller"
            className="game-controller-ranger"
            movementLabel="Move ranger"
            onInputChange={(input) => {
              controllerInputRef.current = createRangerControllerInput(
                controllerInputRef.current,
                { ...input, paused: false }
              );
              if (input.pause) {
                pauseMission();
              }
            }}
          />
        ) : null}

        {showTouchControls && isPaused ? (
          <GameController
            actions={[{ control: 'pause', label: 'Resume mission', text: 'Resume', className: 'ranger-pause-action' }]}
            ariaLabel="Ranger pause controller"
            className="game-controller-ranger"
            movementLabel="Move ranger"
            onInputChange={(input) => {
              controllerInputRef.current = createRangerControllerInput(
                controllerInputRef.current,
                { ...input, paused: true }
              );
              if (input.pause) {
                resumeMission();
              }
            }}
          />
        ) : null}
      </section>
    </main>
  );
}

export default RangerGame;
